import { eventTypes, htmlSelectors, mediaTypes, vtoTypes } from '../constants/vto';
import { Controller } from '../utils/controller';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ymkAsyncInit: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    YMK: any;
    debugVTO?: () => void;
  }
}

interface IFoundationShade {
  hex?: string;
  cooler?: string;
  lighter?: string;
  guid?: string;
  darker?: string;
  warmer?: string;
}

const foundationShades: IFoundationShade[] = [
  { hex: '#e5ad8a' },
  { hex: '#f2bf91' },
  { hex: '#c48f56' },
  { hex: '#c27440' },
  { hex: '#d69060' }
];

export default class extends Controller {
  static targets = [
    'productCarousel',
    'vtoContainer',
    'launchButton',
    'vtoMobileWrapper',
    'vtoShadeCarousel',
    'variantShade',
    'shadesContainer',
    'shadesDropdownText',
    'shadesDropdownSelector',
    'shadesTab',
    'trigger',
    'pagination',
    'shadeDescription'
  ];
  static requiredTargets = [];

  static values = {
    autoOpen: Boolean,
    foundationShades: {
      type: Object,
      default: {}
    },
    locale: {
      type: String,
      default: 'en_US'
    },
    isMobile: Boolean,
    isYMKModuleLoaded: Boolean,
    isYMKModuleOpen: Boolean,
    shadeLocalizedPath: {
      type: String,
      default: ''
    },
    shades: {
      type: Array,
      default: []
    },
    activeVariant: String,
    apikey: String,
    id: String,
    type: String,
    skincareResults: Array,
    productIdList: {
      type: Array,
      default: ['22830']
    },
    width: Number,
    height: Number,
    enabled: {
      type: Boolean,
      default: false
    },
    brand: String
  };

  // TARGETS
  declare paginationTarget: HTMLDivElement;
  declare shadeDescriptionTarget: HTMLSpanElement;
  declare launchButtonTarget: HTMLButtonElement;
  declare productCarouselTarget: HTMLDivElement;
  declare shadesDropdownTextTarget: HTMLSpanElement;
  declare vtoContainerTarget: HTMLDivElement;
  declare variantShadeTargets: HTMLAnchorElement[];
  declare shadesContainerTarget: HTMLDivElement;
  declare shadesDropdownSelectorTargets: HTMLAnchorElement[];
  declare shadesTabTargets: HTMLButtonElement[];
  declare triggerTarget: HTMLButtonElement;
  declare hasLaunchButtonTarget: boolean;
  declare hasProductCarouselTarget: boolean;
  declare hasShadesDropdownTextTarget: boolean;
  declare hasVtoContainerTarget: boolean;
  declare hasVariantShadeTargets: boolean;
  declare hasShadesContainerTarget: boolean;
  declare hasShadesDropdownSelectorTargets: boolean;
  declare hasTriggerTarget: boolean;
  declare hasPaginationTarget: boolean;
  declare hasShadeDescriptionTarget: boolean;

  // VALUES
  private activeVariantValue: string | undefined;
  private apikeyValue: string;
  private foundationShadesValue: IFoundationShade;
  private idValue: string;
  private isYMKModuleLoadedValue: boolean;
  private enabledValue: boolean;
  private localeValue: string;
  private mediaValue: string;
  private payloadEventValueCameraEnd: number;
  private payloadEventValueCameraStart: number;
  private productIdListValue: string[];
  private shadeLocalizedPathValue: string;
  private typeValue: string;
  private brandValue: string;

  private shadesObserver: MutationObserver | null = null;
  private iframeObserver: MutationObserver | null = null;

  initialize() {
    if (this.enabledValue) {
      this.loadYMK();
      this.onIframeAdded(this.observeScreenChanges.bind(this));
      window.debugVTO = this.debugVTO;
    }
  }

  onIframeAdded(callback: (iframe: HTMLIFrameElement) => void) {
    const observer = new MutationObserver(mutations => {
      const iframe = mutations
        .flatMap(m => Array.from(m.addedNodes))
        .filter(n => n instanceof Element)
        .map(node => node.querySelector('iframe'))
        .find(el => el instanceof HTMLIFrameElement);

      if (iframe) {
        callback(iframe);
        observer.disconnect();
      }
    });

    observer.observe(this.vtoContainerTarget, { childList: true, subtree: true });
  }

  observeScreenChanges(iframe) {
    const iframeDocument = iframe.contentWindow?.document
    const frameContent = iframeDocument?.querySelector('.frame-content') as HTMLDivElement;
    // This needs to be executed to resize launcher page on initial render
    this.resizeVTO('INITIAL_RESIZING')

    if (this.iframeObserver) {
      this.iframeObserver.disconnect();
    }

    this.iframeObserver = new MutationObserver(mutations => {
      if (mutations.some(this.nodeChanged)) {
        this.resizeVTO("CHANGES_DETECTED")
      }
    });

    this.iframeObserver.observe(frameContent, { childList: true, subtree: true });
  }

  nodeChanged(mutation: MutationRecord) {
    return mutation.addedNodes.length > 0
  }

  debugVTO() {
    Object.values(eventTypes).forEach(eventName => {
      window.YMK.addEventListener(eventName, (e) => {
        console.log(`🚨 VTO Event: ${eventName}`, e || {});
      });
    });

    console.log("🚨 VTO debugger initialized. Listening to all VTO events...");
  }

  isMobile() {
    return screen.width < 1024;
  }

  isDesktop() {
    return !this.isMobile();
  }

  launchVTO() {
    this.disableLauncher();
    this.hidePDPFeatures();
    this.createYMKElement();

    if (this.hasVTOLoaded()) {
      this.openVTOByType(this.typeValue);
    }

    window.ymkAsyncInit = this.ymkAsyncInit.bind(this);
    window.ymkAsyncInit();
  }

  ymkAsyncInit() {
    const width = this.isMakeup() ? this.vtoContainerTarget.offsetWidth : screen.width
    const height = this.isMakeup() ? this.vtoContainerTarget.offsetHeight : screen.height;

    window.YMK.init({
      autoOpen: !this.isSkincare(),
      showCloseBtnOnLauncher: true,
      language: this.languageMapper(this.localeValue),
      height,
      width,
      ...(this.isFoundation() && { productIdList: this.productIdListValue })
    });

    if (this.isFoundation() || this.isSkincare()) {
      this.openVTOByType(this.typeValue);
    }

    this.trackEvent({ event_action: 'cta', vto_type: this.typeValue, vto_action: 'cta' });
    this.addYMKEventListeners();
  };

  hasVTOLoaded() {
    return this.isYMKModuleLoadedValue;
  }

  trackEvent(detail) {
    window.dispatchEvent(
      new CustomEvent(eventTypes.VTO_ACTIONS_TRACKING, {
        detail: detail
      }));
  }

  disableLauncher() {
    if (this.hasLaunchButtonTarget) {
      this.launchButtonTarget.disabled = true;
    }
  }

  hidePDPFeatures() {
    if (this.hasVtoContainerTarget) {
      this.vtoContainerTarget.style.display = '';
    }

    if (this.hasProductCarouselTarget) {
      this.productCarouselTarget.style.visibility = 'hidden';
    }

    if (this.hasPaginationTarget) {
      this.paginationTarget.style.visibility = 'hidden';
    }

    // hide swiper elements when VTO is active
    const swiperElements = this.element.querySelectorAll<HTMLElement>(
      '.mobile-image-zoom-button,' +
      ' .product-details-badge-text,' +
      ' .swiper-button-next,' +
      ' .swiper.pincer-swiper.swiper-container,' +
      ' .swiper-button-prev'
    );

    swiperElements.forEach(e => e.style.visibility = 'hidden');
  }

  loadYMK() {
    if (!this.idValue || !this.apikeyValue) {
      return;
    }

    const apiUrl = `https://plugins-media.makeupar.com/${this.idValue}/sdk.js?apiKey=${this.apikeyValue}`;
    const scriptLoaded = Array.from(document.getElementsByTagName('script')).find(
      (script) => script.src === apiUrl
    );

    if (scriptLoaded) {
      return;
    }

    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.async = true;
    s.src = apiUrl;
    document.body.appendChild(s);
  }

  getURLSubdirectoryByType(type) {
    switch (type) {
      case vtoTypes.MAKEUP:
        return '';
      case vtoTypes.FOUNDATION:
        return '-shadefinder';
      case vtoTypes.SKINCARE:
        return '-skincare';
      default:
        return '';
    }
  }

  openVTOByType(type) {
    switch (type) {
      case vtoTypes.MAKEUP:
        return window.YMK.open();
      case vtoTypes.FOUNDATION:
        return window.YMK.open(true, null, true);
      case vtoTypes.SKINCARE:
        return window.YMK.openSkincare('capture');
      default:
        return window.YMK.open();
    }
  }

  createYMKElement() {
    const ymkElement = document.getElementById(htmlSelectors.MODULE);
    if (ymkElement != null) {
      return;
    }

    const YMKElement = document.createElement('div');
    const height = this.vtoContainerTarget.offsetHeight;

    YMKElement.id = htmlSelectors.MODULE;
    YMKElement.style.height = height + 'px';

    this.vtoContainerTarget.appendChild(YMKElement);
  }

  eventToMedia(event) {
    const eventToMedia = {
      [eventTypes.LIVE_CAM_ICON_CLICKED]: mediaTypes.CAMERA,
      [eventTypes.CHOOSE_MODEL_ICON_CLICKED]: mediaTypes.MODEL,
      [eventTypes.UPLOAD_PHOTO_ICON_CLICKED]: mediaTypes.UPLOAD
    };

    return eventToMedia[event];
  }

  addYMKEventListeners() {
    const vtoEvents = [
      eventTypes.CHOOSE_MODEL_ICON_CLICKED,
      eventTypes.LIVE_CAM_ICON_CLICKED,
      eventTypes.LOAD_IMAGE_BY_MODEL,
      eventTypes.UPLOAD_PHOTO_ICON_CLICKED,
      eventTypes.CAMERA_CLOSED,
      eventTypes.CAMERA_FAILED,
      eventTypes.CAMERA_OPENED,
      eventTypes.COMPARE,
      eventTypes.COMPARE_DISABLED,
      eventTypes.SPLIT_FOUR_WAY,
      eventTypes.SPLIT_FOUR_WAY_DISABLED,
      eventTypes.PHOTO_LOADED
    ];

    vtoEvents.forEach((event) => {
      window.YMK.addEventListener(event, () => {
        this.mediaValue = this.eventToMedia(event);
        this.trackEvent({
          event_action: event,
          vto_type: this.typeValue,
          vto_media_type: this.mediaValue
        })
      });
    });

    window.YMK.addEventListener(eventTypes.LOADED, () => {
      this.isYMKModuleLoadedValue = true;
      this.payloadEventValueCameraEnd = performance.now();
      const eventValue = Math.floor(this.payloadEventValueCameraEnd - this.payloadEventValueCameraStart);

      if (this.isMakeup()) {
        window.YMK.applyMakeupBySku(this.activeVariantValue);
      }
    });

    if (this.isMakeup()) {
      this.attachVTOEventToShades();

      // Shade tabs re-render the shade list, so we use a MutationObserver
      // to reassign event listeners to new elements as they are added
      this.shadesObserver = new MutationObserver(() => {
        this.attachVTOEventToShades();
      });
      this.shadesObserver.observe(this.shadesContainerTarget, { childList: true, subtree: true });
    }

    window.YMK.addEventListener(eventTypes.SKIN_SHADE_FOUND, (result) => {
      this.foundationShadesValue = result.readableRecommendations[0];
      window.YMK.applyMakeupBySku(result.readableRecommendations[0].guid);
      this.generateFoundationShadeOverlay();
    });

    window.YMK.addEventListener(eventTypes.SKIN_ANALYSIS_FINISHED, () => {
      document.body.style.overflow = '';
    });

    window.YMK.addEventListener(eventTypes.CLOSED, () => {
      this.vtoContainerTarget.style.display = '';
      this.launchButtonTarget.disabled = false;
      this.payloadEventValueCameraEnd = performance.now();

      this.trackEvent({
        event_action: eventTypes.CLOSED,
        vto_type: this.typeValue,
        vto_media_type: this.mediaValue,
        event_value: Math.floor(this.payloadEventValueCameraEnd - this.payloadEventValueCameraStart)
      })

      if (this.shadesObserver) {
        this.shadesObserver.disconnect();
        this.shadesObserver = null;
      }

      if (this.iframeObserver) {
        this.iframeObserver.disconnect();
        this.iframeObserver = null;
      }

      if (this.shadeLocalizedPathValue) {
        window.location.replace(this.shadeLocalizedPathValue);
        // if location.replace fails force the page to reload after two seconds
        setTimeout(() => {
          location.reload();
        }, 2000);
      } else {
        this.resetYMK();
      }
    });
  }

  attachVTOEventToShades() {
    for (const [index, element] of this.variantShadeTargets.entries()) {
      const vtoEnabled = element.dataset['vtoShadeEnabled'];
      delete element.dataset['phxLink'];
      delete element.dataset['phxLinkState'];

      delete this.shadesDropdownSelectorTargets[index].dataset['phxLink'];
      delete this.shadesDropdownSelectorTargets[index].dataset['phxLinkState'];
      this.shadesDropdownSelectorTargets[index].dataset.action =
        'shades-dropdown#toggleListDisplay';

      const localizedPath = element.href;
      const label = element.querySelector('label');
      element.href = '#';
      this.shadesDropdownSelectorTargets[index].href = '#';

      if (vtoEnabled) {
        const selectedSku = element.dataset['vtoSelectedSku'];
        const vtoSku = element.querySelector('input')?.value;
        const shadeDiv = element.querySelector('[data-shade]') as HTMLDivElement;
        const skuShade = shadeDiv?.dataset['shade'];
        const description = element.dataset['shadeDescription'];

        if (vtoSku === this.activeVariantValue) {
          this.shadeLocalizedPathValue = localizedPath;
        }

        if (label) {
          label.style.cursor = 'pointer';
        }

        element.dataset.action = 'product#updateSelectedSkuViaEvent';
        element.dataset.skuid = selectedSku;

        const changeVTOShade = () => {
          this.activeVariantValue = vtoSku;
          this.shadeLocalizedPathValue = localizedPath;
          this.shadesDropdownTextTarget.innerText = label?.title || '';
          this.setDropdownColor(skuShade);
          this.setDescription(description);
          this.unselectAll();
          this.setSelected(element, index);
          window.YMK.applyMakeupBySku(vtoSku);
          this.updateShadeParam(label?.title);
        };

        element.onclick = changeVTOShade;
        this.shadesDropdownSelectorTargets[index].onclick = changeVTOShade;
      } else {
        element.style.opacity = '0.6';

        if (label) {
          label.style.cursor = 'not-allowed';
        }
      }
    }
  }

  updateShadeParam(shade) {
    if (!shade) {
      return;
    }

    const url = new URL(window.location.href);
    const shadeParam = shade.replace(/\s+/g, "_") || ""
    url.searchParams.set('shade', shadeParam);
    window.history.replaceState({}, '', url.toString());

  }

  setDescription(description) {
    if (this.hasShadeDescriptionTarget && description) {
      this.shadeDescriptionTarget.textContent = description;
    }
  }

  setDropdownColor(color) {
    const shadesDropdown = this.triggerTarget.querySelector('[data-shade]') as HTMLDivElement;

    if (shadesDropdown && color) {
      shadesDropdown.style.backgroundColor = color;
    }
  }

  unselectAll() {
    this.variantShadeTargets.forEach((el, i) => this.removeSelected(el, i));
  }

  removeSelected(shade, index) {
    const selectedVariant = shade.querySelector('.shade-selected');

    if (selectedVariant) {
      selectedVariant.classList.remove('shade-selected');
    }

    const shadEl = this.shadesDropdownSelectorTargets[index].parentNode as HTMLElement;
    shadEl.classList.remove('checked');
  }

  setSelected(shade, index) {
    const selectedVariant = shade.querySelector('.shade-swatch');

    if (selectedVariant) {
      selectedVariant.classList.add('shade-selected');
    }

    const shadSelectorEl = this.shadesDropdownSelectorTargets[index].parentNode as HTMLElement;
    shadSelectorEl.classList.add('checked');
  }

  resetYMK() {
    const ymkElement = document.getElementById(htmlSelectors.MODULE);
    this.vtoContainerTarget.style.display = 'none';
    this.productCarouselTarget.style.visibility = 'visible';
    this.paginationTarget.style.visibility = 'visible';
    ymkElement?.remove();
    window.YMK.reset();
  }

  generateFoundationShadeButton(hex, sku) {
    const button = document.createElement('button');
    button.style.background = hex;
    button.onclick = () => window.YMK.applyMakeupBySku(sku);

    return button;
  }

  generateFoundationShadeOverlay() {
    const shadeContainer = document.createElement('div');

    const cooler = this.generateFoundationShadeButton(
      foundationShades[0].hex,
      this.foundationShadesValue.cooler
    );
    const lighter = this.generateFoundationShadeButton(
      foundationShades[1].hex,
      this.foundationShadesValue.lighter
    );
    const best = this.generateFoundationShadeButton(
      foundationShades[2].hex,
      this.foundationShadesValue.guid
    );
    const darker = this.generateFoundationShadeButton(
      foundationShades[3].hex,
      this.foundationShadesValue.darker
    );
    const warmer = this.generateFoundationShadeButton(
      foundationShades[4].hex,
      this.foundationShadesValue.warmer
    );

    shadeContainer.appendChild(cooler);
    shadeContainer.appendChild(lighter);
    shadeContainer.appendChild(best);
    shadeContainer.appendChild(darker);
    shadeContainer.appendChild(warmer);

    const YMKElement = document.getElementById(htmlSelectors.MODULE);
    const YMKiFrame = document.getElementById(htmlSelectors.MODULE_IFRAME);

    const wrapper = document.createElement('div');

    wrapper.appendChild(shadeContainer);

    const addToBagTemplate = document.getElementById('vto-add-to-bag-button');
    const addToBagTemplateContent = (addToBagTemplate as HTMLTemplateElement).content;
    const clonedContent = addToBagTemplateContent.cloneNode(true);

    wrapper.append(shadeContainer);
    wrapper.appendChild(clonedContent);

    if (YMKiFrame) {
      YMKiFrame.style.height = `${screen.height - 208}px`;
    }
    YMKElement?.appendChild(wrapper);
  }

  resizeVTO(event_action: string, event_value = 0) {
    const YMKElement = document.getElementById(htmlSelectors.MODULE);
    const DELAY = 500;
    if (!YMKElement) {
      return;
    }
    this.payloadEventValueCameraStart = performance.now();

    this.trackEvent({
      event_action,
      vto_type: this.typeValue,
      vto_media_type: this.mediaValue,
      event_value
    });

    // element is hidden so we don't see its wrong version. Once it's re-styled, we show it again.
    YMKElement.style.display = 'none';

    setTimeout(() => {
      const YMKiFrame = document.getElementById(htmlSelectors.MODULE_IFRAME) as HTMLIFrameElement;

      if (YMKiFrame) {
        const iframeDocument = YMKiFrame?.contentWindow?.document;
        const frameContent = iframeDocument?.querySelector('.frame-content') as HTMLDivElement;
        this.applyScreenAdjustments(frameContent);
        this.setElementsDimensions(YMKElement, YMKiFrame, iframeDocument)
      }

      // make element visible again
      YMKElement.style.display = '';
    }, DELAY);
  }

  applyScreenAdjustments(nodeElement) {
    const innerHTML = nodeElement.innerHTML;
    if (this.isLauncher(innerHTML)) {
      this.resizeLauncherPage(nodeElement);
    } else if (this.isChooseAModel(innerHTML)) {
      this.resizeModelImages(nodeElement);
    } else if (this.isSplitFourWay(innerHTML)) {
      this.resizeSplitFourWay(nodeElement);
    }
  }

  isLauncher(html) {
    return [
      "Try It Now",
      "Essayez-le maintenant !",
      "¡Pruébalo!",
      "Jetzt ausprobieren",
      "Prova adesso",
      "Prøv det nu",
      "Prøv nå",
      "Wypróbuj teraz",
      "SPIELE MIT FARBE",
      "PLAY WITH COLOUR",
      "ZKOUŠET ODSTÍNY",
      "GIOCA CON IL COLORE",
      "BAW SIĘ KOLOREM",
      "¡DIVIÉRTETE PROBANDO!",
      "メイクアップモードを選択",
      "Escoge tu Probador",
      "Escolher",
      "체험 모드를 선택하세요",
      "Sélectionnez votre mode d'essai",
      "Play With Color",
      "选择上妆模式",
      "トライ機能を選択",
      "Wähle Try-On Erlebnis",
      "Jouez avec la couleur",
      "Speel met kleur",
      "PLAY WITH COLOR",
      "Scegli l'Esperienza Prova",
      "Baw się kolorem",
      "Hrajte si s barvami",
      "Spiele mit Farben",
      "Juega con el color",
      "Gioca con il colore",
      "TESTER DIFFÉRENTES TEINTES"
    ].some(label => html.includes(label))
  }

  isChooseAModel(html) {
    return [
      "CHOOSE A MODEL",
      "CHOISIR UN MODÈLE",
      "Escoge una Modelo",
      "MODEL AUSWÄHLEN",
      "SCEGLI MODELLO",
      "VÆLG EN MODEL",
      "VELG EN MODELL", 
      "WYBIERZ MODELKĘ",
      "WÄHLE EIN MODEL",
      "VYBRAT MODELKU",
      "SCEGLI UN MODELLO",
      "ELIGE UNA MODELO",
      "Escolha um modelo",
      "模特兒上妝",
      "模特儿上妆",
      "モデルを選択",
      "모델 선택",
      "Wähle ein Model",
      "KIES EEN MODEL",
      "Choisir un mannequin",
      "Scegli un Modello",
      "モデルを選ぶ",
      "WYBIERZ MODELKĘ/MODELA",
      "VYBRAT MODELA/MODELKU",
      "ELIGE A UNA MODELO",
      "SCEGLI UNA MODELLA"
    ].some(label => html.includes(label))
  }

  isSplitFourWay(html) {
    return [
      "Add Another Shade",
      "Ajouter une teinte",
      "Voeg nog een tint toe",
      "Anderen Farbton hinzufügen",
      "Dodaj kolejny odcień",
      "Přidat další odstín",
      "Añade otro tono",
      "Aggiungi un'altra tonalità",
    ].some(label => html.includes(label))
  }

  setElementsDimensions(YMKElement, YMKiFrame, iframeDocument) {
    const height = this.vtoContainerTarget.offsetHeight;
    const width = this.vtoContainerTarget.offsetWidth;

    const YMKElementFirstDiv = YMKElement.querySelector('div');
    if (YMKElementFirstDiv) {
      YMKElementFirstDiv.style.height = height + 'px';
      YMKElementFirstDiv.style.width = width + 'px';
    }

    const frameContent = iframeDocument?.querySelector('.frame-content');
    const frameFirstDiv = frameContent && frameContent.querySelector('div');
    if (frameFirstDiv) {
      frameFirstDiv.style.height = height + 'px';
      frameFirstDiv.style.width = width + 'px';
    }

    YMKiFrame.style.height = height + 'px';
    YMKiFrame.style.width = width + 'px';

    const YMKVideoInput = document.getElementById('YMK-video-input') as HTMLIFrameElement;
    if (YMKVideoInput) {
      YMKVideoInput.style.height = height + 'px';
      YMKVideoInput.style.width = width + 'px';
    }

    const resultCanvas = iframeDocument?.getElementById('result-canvas');
    if (resultCanvas) {
      resultCanvas.style.height = height + 'px';
      resultCanvas.style.width = width + 'px';
    }
  }

  languageMapper(locale) {
    const languageMap = {
      cs_: 'cze',
      de_: 'deu',
      da_: 'dnk',
      en_: 'enu',
      es_: 'esp',
      fi_: 'fin',
      fr_: 'fra',
      it_: 'ita',
      ja_: 'jpn',
      ko_: 'kor',
      nl_: 'dut',
      no_: 'nor',
      pl_: 'pol',
      pt_: 'ptb',
      zh_: 'cht',
      zhhans: 'chs',
      zhhant: 'cht'
    };

    for (const key in languageMap) {
      if (locale.includes(key)) {
        return languageMap[key];
      }
    }

    return 'enu';
  }

  private isMakeup() {
    return this.typeValue === vtoTypes.MAKEUP;
  }

  private isSkincare() {
    return this.typeValue === vtoTypes.SKINCARE;
  }

  private isFoundation() {
    return this.typeValue === vtoTypes.FOUNDATION;
  }

  private resizeSplitFourWay(frameContent) {
    if (this.brandValue == "CL") {
      const height = this.vtoContainerTarget.offsetHeight;
      const buttonsContainer = frameContent.querySelector(".frame-content > div > div > div:nth-child(1)");
      buttonsContainer.style.height = `${height}px`;
    }
  }

  private resizeLauncherPage(frameContent) {
    if (this.brandValue == "MC" && (this.isMobile() || !window.matchMedia('(hover: hover) and (pointer: fine)').matches)) {
      const contentContainer = frameContent.querySelector(".frame-content > div:nth-child(2) > div > div:nth-child(2)");
      contentContainer.style.top = '7%';

      const title = contentContainer.firstElementChild;
      title.style.padding = '0 0 10px 0';
      title.style.fontSize = '20px';
      title.style.lineHeight = '1rem';
      title.style.maxWidth = '100%';
      title.style.textAlign = 'center';

      const orButton = frameContent.querySelector(".frame-content > div:nth-child(2) > div > div:nth-child(2) > div:nth-child(3) > div:nth-child(3)")
      orButton.style.padding = '13px 10px';

      if (contentContainer) {
        const lastChild = contentContainer.lastElementChild;
        if (lastChild) {
          lastChild.style.padding = '0'
        }
      }
    }

    if (this.brandValue == "CL") {
      const title = frameContent.querySelector(".frame-content > div > div > div:nth-child(2) > div:nth-child(1)")
      title.style.fontSize = this.isMobile() ? '25px' : '30px';
    }

    if (this.brandValue == "EL") {
      const container = frameContent.querySelector(".frame-content > div > div > div:nth-child(2)")
      container.style.transform = 'translateY(-50%)';
      if (this.isMobile()) {
        container.style.padding = '2rem';
      }
    }
  }

  private resizeModelImages(frameContent) {
    if (this.brandValue == "CL") {
      const generalContainer = frameContent.querySelector(".frame-content > div > div > div:nth-child(2)");
      generalContainer.style.top = '13%';

      const gridContainer = frameContent.querySelector(".frame-content > div > div > div:nth-child(2) > div > div:nth-child(2)");
      gridContainer.style.transform = this.isMobile() ? 'scale(0.6)' : 'scale(0.7)';
      gridContainer.style.transformOrigin = 'top center';
      gridContainer.style.maxHeight = 'unset';
    }

    if (this.brandValue == "EL") {
      if (this.isMobile() || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        const container = frameContent.querySelector("body > div > div > div > div > div:nth-child(3)")
        container.style.top = '10%';
        container.style.transform = 'none';

        const imagesContainer = container?.querySelector("div:nth-child(2)");
        imagesContainer.style.display = "grid";
        imagesContainer.style.gridTemplateColumns = "repeat(3, 1fr)";
        imagesContainer.style.transform = 'scale(0.7)';
        imagesContainer.style.transformOrigin = 'top';

        const imgs = frameContent?.querySelectorAll('img');
        for (const img of imgs) {
          const imgContainer = img.parentElement.parentElement;
          imgContainer.style.width = 'auto';
          imgContainer.style.height = 'auto';
          imgContainer.style.float = 'none';
        }
      }
    }
  }
}
